/* eslint-disable react/jsx-no-constructed-context-values */
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { IconContext } from 'react-icons';
import { useMutation, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import {
  RiShieldLine,
  RiBugLine,
  RiHome7Line,
  RiHome7Fill,
  RiSearchLine,
  RiSearchFill,
  RiMailLine,
  RiMailFill,
  RiNotificationLine,
  RiNotificationFill,
  RiBookmarkLine,
  RiBookmarkFill,
  RiHeart3Line,
  RiHeart3Fill,
  RiUserLine,
  RiUserFill,
  RiSettingsLine,
  RiSettingsFill,
  RiMoreFill,
  RiArrowDownSLine,
  RiArrowUpSLine,
} from 'react-icons/ri';
import toast from 'react-hot-toast';

import { useAuth } from '../../contexts/auth-context';
import { useBranding } from '../../contexts/branding-context';
import { useDebugTools } from '../../contexts/debug-tools-context';
import useNotifications from '../../hooks/useNotifications';
import BrandLogo from '../BrandLogo';
import Avatar from '../Avatar';
import SeloApoiadorInline from '../SeloApoiadorInline';

import DropDown from './DropDown/DropDown';
import DropDownItem from './DropDown/DropDownItem';

import axios from '../../utils/axios';
import {
  refreshNavigationTarget,
  scrollAppToTop,
} from '../../utils/navigation-refresh';
import { openNativePostComposer } from '../../utils/mobile-native';

const SideNav = ({ onNavigate = () => {} }) => {
  const [, setModalOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { user, logout } = useAuth();
  const { isDebugToolsOpen, toggleDebugTools } = useDebugTools();
  const {
    settings: { systemName },
  } = useBranding();
  const { t } = useTranslation();
  const notificationData = useNotifications();
  const isHomeActive = location.pathname === '/home';
  const isMoreActive =
    location.pathname.startsWith('/settings') ||
    location.pathname.startsWith('/admin') ||
    isDebugToolsOpen;

  useEffect(() => {
    if (isMoreActive) {
      setIsMoreMenuOpen(true);
    }
  }, [isMoreActive]);

  const logoutMutation = useMutation(
    () => {
      return axios.post('/api/auth/logout');
    },
    {
      onSuccess: () => {
        logout();
        toast.dismiss();
        queryClient.removeQueries();
        navigate('/');
      },
    }
  );

  const openModal = async () => {
    const handledByNativeComposer = await openNativePostComposer();
    if (handledByNativeComposer) {
      return;
    }

    setModalOpen(true);
    navigate('/compose/post', {
      state: {
        backgroundLocation: location,
      },
      replace: true,
    });
  };

  const handleDropDownOpen = (evt) => {
    setAnchorEl(evt.currentTarget);
  };

  const handleDropDownClose = () => {
    setAnchorEl(null);
  };

  const handleHomeNavigation = () => {
    onNavigate();
    scrollAppToTop();
    queryClient.removeQueries(['posts', 'feed']);
    queryClient.invalidateQueries(['posts', 'feed']);
    navigate('/home', {
      state: {
        openFeedTab: 'for-you',
        homeNavigationAt: Date.now(),
      },
    });
  };

  const handleRefreshableNavigation = ({
    event,
    isActive,
    queryKeys,
    state,
    to,
  }) => {
    onNavigate();
    refreshNavigationTarget({
      event,
      isActive,
      navigate,
      queryClient,
      queryKeys,
      state,
      to,
    });
  };

  const handleFinalMenuAction = (action) => {
    onNavigate();
    if (typeof action === 'function') {
      action();
    }
  };

  return (
    <div className="px-4 py-6 border-r border-on-surface/20 min-h-full flex flex-col justify-between sm:items-center lg:items-stretch">
      <div>
        <button
          type="button"
          onClick={handleHomeNavigation}
          className="text-primary px-4 mb-3 flex items-center gap-3 hover:opacity-80 transition-opacity"
        >
          <BrandLogo
            compact
            iconSize="36px"
            imageClassName="h-9 w-9 object-contain"
          />
          <span className="hidden text-on-surface font-bold text-lg lg:inline">
            {systemName}
          </span>
        </button>
        <nav className="navigation sm:flex sm:flex-col sm:items-center lg:block">
          <ul>
            <li className="text-on-surface mb-3">
              <button
                type="button"
                onClick={handleHomeNavigation}
                aria-label="Home"
              >
                <div
                  className={`flex items-center gap-3 rounded-3xl px-4 py-2 w-fit ${
                    isHomeActive && 'bg-on-surface/10'
                  }`}
                >
                  <span>
                    <IconContext.Provider
                      value={{
                        size: '24px',
                        style: {
                          color: 'inherit',
                        },
                      }}
                    >
                      {isHomeActive ? <RiHome7Fill /> : <RiHome7Line />}
                    </IconContext.Provider>
                  </span>
                  <span
                    className={`text-xl ${
                      isHomeActive && 'font-semibold'
                    } sm:hidden lg:inline`}
                  >
                    {t('nav.home')}
                  </span>
                </div>
              </button>
            </li>
            <li className="text-on-surface mb-3">
              <NavLink
                to="/search"
                aria-label="Search"
                onClick={(event) =>
                  handleRefreshableNavigation({
                    event,
                    isActive: location.pathname === '/search',
                    queryKeys: ['search'],
                    to: '/search',
                  })
                }
              >
                {({ isActive }) => (
                  <div
                    className={`flex items-center gap-3 rounded-3xl px-4 py-2 w-fit ${
                      isActive && 'bg-on-surface/10'
                    }`}
                  >
                    <span>
                      <IconContext.Provider
                        value={{
                          size: '24px',
                          style: {
                            color: 'inherit',
                          },
                        }}
                      >
                        {isActive ? <RiSearchFill /> : <RiSearchLine />}
                      </IconContext.Provider>
                    </span>
                    <span
                      className={`text-xl ${
                        isActive && 'font-semibold'
                      } sm:hidden lg:inline`}
                    >
                      {t('nav.explore')}
                    </span>
                  </div>
                )}
              </NavLink>
            </li>
            <li className="text-on-surface mb-3">
              <NavLink
                to="/notifications"
                aria-label="Notifications"
                onClick={(event) =>
                  handleRefreshableNavigation({
                    event,
                    isActive: location.pathname === '/notifications',
                    queryKeys: ['notifications'],
                    to: '/notifications',
                  })
                }
              >
                {({ isActive }) => (
                  <div
                    className={`relative flex items-center gap-3 rounded-3xl px-4 py-2 w-fit ${
                      isActive && 'bg-on-surface/10'
                    }`}
                  >
                    <span>
                      <IconContext.Provider
                        value={{
                          size: '24px',
                          style: {
                            color: 'inherit',
                          },
                        }}
                      >
                        {isActive ? (
                          <RiNotificationFill />
                        ) : (
                          <RiNotificationLine />
                        )}
                      </IconContext.Provider>
                    </span>
                    <span
                      className={`text-xl ${
                        isActive && 'font-semibold'
                      } sm:hidden lg:inline`}
                    >
                      {t('nav.notifications')}
                    </span>
                    {notificationData.isSuccess &&
                      notificationData.data.pages[0].info.unReadNotifications >
                        0 && (
                        <span className="absolute top-1 left-8 w-5 h-5 rounded-full bg-primary flex items-center justify-center text-xs">
                          <span>
                            {
                              notificationData.data.pages[0].info
                                .unReadNotifications
                            }
                          </span>
                        </span>
                      )}
                  </div>
                )}
              </NavLink>
            </li>
            <li className="text-on-surface mb-3">
              <NavLink
                to="/apoiador"
                aria-label="Apoiador"
                onClick={(event) =>
                  handleRefreshableNavigation({
                    event,
                    isActive: location.pathname.startsWith('/apoiador'),
                    queryKeys: ['assinatura-home'],
                    to: '/apoiador',
                  })
                }
              >
                {({ isActive }) => (
                  <div
                    className={`flex items-center gap-3 rounded-3xl px-4 py-2 w-fit ${
                      isActive && 'bg-on-surface/10'
                    }`}
                  >
                    <span>
                      <IconContext.Provider
                        value={{
                          size: '24px',
                          style: {
                            color: 'inherit',
                          },
                        }}
                      >
                        {isActive ? <RiHeart3Fill /> : <RiHeart3Line />}
                      </IconContext.Provider>
                    </span>
                    <span
                      className={`text-xl ${
                        isActive && 'font-semibold'
                      } sm:hidden lg:inline`}
                    >
                      {t('nav.apoiador')}
                    </span>
                  </div>
                )}
              </NavLink>
            </li>
            <li className="text-on-surface mb-3">
              <NavLink
                to="/messages"
                aria-label="Messages"
                onClick={(event) =>
                  handleRefreshableNavigation({
                    event,
                    isActive: location.pathname.startsWith('/messages'),
                    queryKeys: ['messages'],
                    to: '/messages',
                  })
                }
              >
                {({ isActive }) => (
                  <div
                    className={`flex items-center gap-3 rounded-3xl px-4 py-2 w-fit ${
                      isActive && 'bg-on-surface/10'
                    }`}
                  >
                    <span>
                      <IconContext.Provider
                        value={{
                          size: '24px',
                          style: {
                            color: 'inherit',
                          },
                        }}
                      >
                        {isActive ? <RiMailFill /> : <RiMailLine />}
                      </IconContext.Provider>
                    </span>
                    <span
                      className={`text-xl ${
                        isActive && 'font-semibold'
                      } sm:hidden lg:inline`}
                    >
                      {t('nav.messages')}
                    </span>
                  </div>
                )}
              </NavLink>
            </li>
            <li className="text-on-surface mb-3">
              <NavLink
                to="/saved"
                aria-label="Saved"
                onClick={(event) =>
                  handleRefreshableNavigation({
                    event,
                    isActive: location.pathname === '/saved',
                    queryKeys: ['saved'],
                    to: '/saved',
                  })
                }
              >
                {({ isActive }) => (
                  <div
                    className={`flex items-center gap-3 rounded-3xl px-4 py-2 w-fit ${
                      isActive && 'bg-on-surface/10'
                    }`}
                  >
                    <span>
                      <IconContext.Provider
                        value={{
                          size: '24px',
                          style: {
                            color: 'inherit',
                          },
                        }}
                      >
                        {isActive ? <RiBookmarkFill /> : <RiBookmarkLine />}
                      </IconContext.Provider>
                    </span>
                    <span
                      className={`text-xl ${
                        isActive && 'font-semibold'
                      } sm:hidden lg:inline`}
                    >
                      {t('nav.saved')}
                    </span>
                  </div>
                )}
              </NavLink>
            </li>
            <li className="text-on-surface mb-3">
              <NavLink
                to={`/${user.username}`}
                aria-label="Profile"
                onClick={(event) =>
                  handleRefreshableNavigation({
                    event,
                    isActive: location.pathname === `/${user.username}`,
                    queryKeys: [
                      ['user', user.username],
                      ['posts', user.id],
                      ['posts', 'replies', user.id],
                      ['posts', 'liked', user.id],
                    ],
                    to: `/${user.username}`,
                  })
                }
              >
                {({ isActive }) => (
                  <div
                    className={`flex items-center gap-3 rounded-3xl px-4 py-2 w-fit ${
                      isActive && 'bg-on-surface/10'
                    }`}
                  >
                    <span>
                      <IconContext.Provider
                        value={{
                          size: '24px',
                          style: {
                            color: 'inherit',
                          },
                        }}
                      >
                        {isActive ? <RiUserFill /> : <RiUserLine />}
                      </IconContext.Provider>
                    </span>
                    <span
                      className={`text-xl ${
                        isActive && 'font-semibold'
                      } sm:hidden lg:inline`}
                    >
                      {t('nav.profile')}
                    </span>
                  </div>
                )}
              </NavLink>
            </li>
            <li className="text-on-surface mb-3">
              <button
                type="button"
                aria-label={t('nav.more')}
                aria-expanded={isMoreMenuOpen}
                onClick={() => setIsMoreMenuOpen((current) => !current)}
                className={`flex items-center gap-3 rounded-3xl px-4 py-2 w-fit ${
                  isMoreActive && 'bg-on-surface/10'
                }`}
              >
                <span>
                  <IconContext.Provider
                    value={{
                      size: '24px',
                      style: {
                        color: 'inherit',
                      },
                    }}
                  >
                    <RiMoreFill />
                  </IconContext.Provider>
                </span>
                <span
                  className={`text-xl ${
                    isMoreActive && 'font-semibold'
                  } sm:hidden lg:inline`}
                >
                  {t('nav.more')}
                </span>
                <span className="sm:hidden lg:inline">
                  {isMoreMenuOpen ? <RiArrowUpSLine /> : <RiArrowDownSLine />}
                </span>
              </button>

              {isMoreMenuOpen && (
                <ul className="mt-2 ml-4 border-l border-on-surface/10 pl-3 sm:ml-0 sm:pl-0 sm:border-l-0 lg:ml-4 lg:border-l lg:pl-3">
                  <li className="text-on-surface mb-2 relative">
                    <NavLink
                      to="/settings"
                      aria-label="Settings"
                      onClick={onNavigate}
                    >
                      {({ isActive }) => (
                        <div
                          className={`flex items-center gap-3 rounded-3xl px-4 py-2 w-fit ${
                            isActive && 'bg-on-surface/10'
                          }`}
                        >
                          <span>
                            <IconContext.Provider
                              value={{
                                size: '24px',
                                style: {
                                  color: 'inherit',
                                },
                              }}
                            >
                              {isActive ? (
                                <RiSettingsFill />
                              ) : (
                                <RiSettingsLine />
                              )}
                            </IconContext.Provider>
                          </span>
                          <span
                            className={`text-xl ${
                              isActive && 'font-semibold'
                            } sm:hidden lg:inline`}
                          >
                            {t('nav.settings')}
                          </span>
                        </div>
                      )}
                    </NavLink>
                  </li>
                  {user.isAdmin && (
                    <>
                      <li className="text-on-surface mb-2 relative">
                        <a
                          href="/xadmin/operacao/dashboard"
                          aria-label="Admin"
                          onClick={onNavigate}
                        >
                          <div className="flex items-center gap-3 rounded-3xl px-4 py-2 w-fit hover:bg-on-surface/10 transition-colors">
                            <span>
                              <IconContext.Provider
                                value={{
                                  size: '24px',
                                  style: {
                                    color: 'inherit',
                                  },
                                }}
                              >
                                <RiShieldLine />
                              </IconContext.Provider>
                            </span>
                            <span className="text-xl sm:hidden lg:inline">
                              {t('nav.admin')}
                            </span>
                          </div>
                        </a>
                      </li>
                      <li className="text-on-surface mb-2 relative">
                        <button
                          type="button"
                          onClick={() =>
                            handleFinalMenuAction(toggleDebugTools)
                          }
                          className={`flex items-center gap-3 rounded-3xl px-4 py-2 w-fit transition-colors ${
                            isDebugToolsOpen && 'bg-on-surface/10'
                          }`}
                          aria-label={
                            isDebugToolsOpen
                              ? 'Fechar painel de debug'
                              : 'Abrir painel de debug'
                          }
                          title={
                            isDebugToolsOpen
                              ? 'Fechar painel de debug'
                              : 'Abrir painel de debug'
                          }
                        >
                          <span>
                            <RiBugLine size={24} />
                          </span>
                          <span
                            className={`text-xl ${
                              isDebugToolsOpen && 'font-semibold'
                            } sm:hidden lg:inline`}
                          >
                            Debug
                          </span>
                        </button>
                      </li>
                    </>
                  )}
                </ul>
              )}
            </li>
          </ul>
          <div className="mt-4">
            <button
              type="button"
              onClick={() => {
                onNavigate();
                openModal();
              }}
              className="bg-primary text-on-primary text-base w-full py-3 rounded-3xl sm:w-12 lg:w-full"
              aria-label="Add Post"
            >
              <span className="sm:hidden lg:inline">{t('nav.post')}</span>
              <span className="hidden sm:inline lg:hidden">+</span>
            </button>
          </div>
        </nav>
      </div>
      <div className="flex gap-2 relative">
        <div
          className="h-10 w-10 overflow-hidden"
          onClick={handleDropDownOpen}
          onKeyDown={handleDropDownOpen}
          role="button"
          tabIndex={0}
        >
          <Avatar
            className="h-full w-full rounded-full object-cover"
            src={user.profile.img}
            name={user.profile.name}
            username={user.username}
            alt="avatar"
          />
        </div>
        <div className="flex justify-between flex-1 items-center sm:hidden lg:flex">
          <div>
            <p className="font-semibold inline-flex items-center gap-1">
              <span>{user.profile.name}</span>
              <SeloApoiadorInline selo={user.apoiadorSelo} size="xs" />
            </p>
            <p className="text-sm text-on-surface/70 -mt-1">@{user.username}</p>
          </div>
          <button
            type="button"
            onClick={handleDropDownOpen}
            className="font-bold"
          >
            ...
          </button>
        </div>
        <DropDown
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleDropDownClose}
        >
          <DropDownItem onClose={handleDropDownClose}>
            <Link to={`/${user.username}`} onClick={onNavigate}>
              <div className="flex gap-2 relative px-4 py-4 border-b border-on-surface/20">
                <div className="h-10 w-10 overflow-hidden">
                  <Avatar
                    className="h-full w-full rounded-full object-cover"
                    src={user.profile.img}
                    name={user.profile.name}
                    username={user.username}
                    alt="avatar"
                  />
                </div>
                <div className="flex justify-between flex-1 items-center">
                  <div>
                    <p className="font-semibold inline-flex items-center gap-1">
                      <span>{user.profile.name}</span>
                      <SeloApoiadorInline selo={user.apoiadorSelo} size="xs" />
                    </p>
                    <p className="text-sm text-on-surface/70 -mt-1">
                      @{user.username}
                    </p>
                  </div>
                </div>
              </div>
            </Link>
          </DropDownItem>
          <DropDownItem onClose={handleDropDownClose}>
            <button
              type="button"
              className="px-4 py-3 w-full text-left"
              onClick={() =>
                handleFinalMenuAction(() => logoutMutation.mutate())
              }
            >
              {t('nav.signout')} @{user.username}
            </button>
          </DropDownItem>
        </DropDown>
      </div>
    </div>
  );
};

SideNav.propTypes = {
  onNavigate: PropTypes.func,
};

export default SideNav;
